// NOTE: Requires 'recharts' library. Run: npm install recharts
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faUserGraduate,
  faFileAlt,
  faChartPie,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { downloadAsCSV } from "../../../Helpers/excelHelpers";
import { toastHelper } from "../../../Helpers/toastHelper";
import ApiServices from "../../Services/ApiServices";
import "../../../assets/css/Reports.css";
import { formatDateSimple } from "../../../Helpers/dateHelpers";
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const Reports = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("exams");
  const [dateRange, setDateRange] = useState("month");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // State for API data
  const [overviewStats, setOverviewStats] = useState({
    total_exams: 0,
    total_students: 0,
    average_score: 0,
    completion_rate: 0,
    recently_added: 0,
  });
  const [monthlyStudents, setMonthlyStudents] = useState([]);
  const [examData, setExamData] = useState([]);
  const [studentData, setStudentData] = useState([]);

  // Add this after your state declarations
  const getTableHeaders = (tab = activeTab) => {
    if (tab === "exams") {
      return {
        title: t('examName'),
        date: t('date'),
        total_students: t('studentCount'),
        average_score: t('averageScore'),
        completion_rate: t('completionRate'),
        passing_rate: t('passingRate'),
      };
    } else {
      return {
        name: t('studentName'),
        exams_taken: t('examsTaken'),
        average_score: t('averageScore'),
        last_exam_date: t('lastExamDate'),
        completion_rate: t('completionRate'),
        average_time_taken: t('averageTimeTaken'),
        improvement: t('improvement'),
      };
    }
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsResponse, examResponse, studentResponse] = await Promise.all([
        ApiServices.get("Dashboard/Report/TeacherOverviewStats"),
        ApiServices.get("Dashboard/Report/Exam"),
        ApiServices.get("Dashboard/Report/Student"),
      ]);

      // Update overview stats
      if (statsResponse.check && statsResponse.data) {
        if (statsResponse.data.teacher_overview_stats) {
          setOverviewStats(statsResponse.data.teacher_overview_stats);
        }
        if (statsResponse.data.monthly_students) {
          setMonthlyStudents(statsResponse.data.monthly_students);
        }
      }

      // Update exam data
      if (examResponse.check && examResponse.data) {
        setExamData(examResponse.data);
      }

      // Update student data
      if (studentResponse.check && studentResponse.data) {
        setStudentData(studentResponse.data);
      }
    } catch (error) {
      toastHelper.error(t('dataLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter data based on search term
  const filteredData =
    activeTab === "exams"
      ? examData.filter((exam) => exam.title.includes(searchTerm))
      : studentData.filter((student) => student.name.includes(searchTerm));

  // Update the handleDownload function
  const handleDownload = () => {
    try {
      let data = activeTab === "exams" ? examData : studentData;

      // Format the data before export
      data = data.map((item) => {
        const formattedItem = { ...item };

        if (activeTab === "exams") {
          formattedItem.date = formatDateSimple(item.date);
        } else {
          formattedItem.last_exam_date = formatDateSimple(item.last_exam_date);
        }

        return formattedItem;
      });

      const headers = getTableHeaders();
      const filename = `${activeTab}-report-${dateRange}-${formatDateSimple(
        new Date()
      )}`;
      downloadAsCSV(data, filename, headers);
      toastHelper.success(t('reportDownloadSuccess'));
    } catch (error) {
      toastHelper.error(t('reportDownloadFailed'));
    }
  };

  // Stat cards config using API data and Arabic labels
  const statCards = [
    {
      icon: faFileAlt,
      value: overviewStats.total_exams,
      label: t('examsTaken'),
    },
    {
      icon: faUserGraduate,
      value: overviewStats.total_students,
      label: t('studentCount'),
    },
    {
      icon: faChartLine,
      value: `${overviewStats.average_score}%`,
      label: t('averageScore'),
    },
    {
      icon: faChartPie,
      value: `${overviewStats.completion_rate}%`,
      label: t('completionRate'),
    },
    {
      icon: faUserPlus,
      value: overviewStats.recently_added,
      label: t('recentlyAddedStudents'),
    },
  ];

  // Prepare chart data from examData (average_score over date)
  const chartData = examData.map((exam) => ({
    date: formatDateSimple(exam.date),
    average_score: exam.average_score,
  }));


  // حساب نسبة النجاح/الرسوب من بيانات الاختبارات الحقيقية
  const totalPassing = examData.reduce((sum, exam) => sum + (exam.passing_rate || 0), 0);
  const totalFailing = examData.length * 100 - totalPassing;
  const passFailPieData = [
    { name: t('passingStudents'), value: totalPassing },
    { name: t('failingStudents'), value: totalFailing }
  ];
  const COLORS = ["#22c55e", "#ef4444"];

  // استخراج أعلى 5 طلاب حسب متوسط الدرجات من بيانات الـ API
  const topStudents = [...studentData]
    .sort((a, b) => b.average_score - a.average_score)
    .slice(0, 5)
    .map(s => ({
      name: s.name.split(' ')[0], // الاسم الأول فقط للوضوح
      avg: s.average_score
    }));

  const examStatusData = [
    { status: t('completed'), count: 120 },
    { status: t('notCompleted'), count: 30 }
  ];

  return (
    <div className="dashboard-bg">
      {/* Stat Cards */}
      <div className="stat-cards">
        {statCards.map((card, i) => (
          <div className="state_t-card" key={i}>
            <div className="icon">
              <FontAwesomeIcon icon={card.icon} />
            </div>
            <div className="value">{card.value}</div>
            <div className="label">{card.label}</div>
          </div>
        ))}
      </div>
      {/* قسم الرسوم البيانية الإحصائية */}
      <div className="charts-grid">
        {/* PieChart نسبة النجاح/الرسوب (بيانات حقيقية) */}
        <CardBox className="minh-220">
          <div className="card-title mb-12">{t('passFailRate')}</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={passFailPieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={70}
                fill="#8884d8"
                label
              >
                {passFailPieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </CardBox>
        {/* BarChart أكثر الطلاب تفوقًا (بيانات حقيقية) */}
        <CardBox className="minh-220">
          <div className="card-title mb-12">{t('topPerformingStudents')}</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topStudents} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="name" stroke="#a0aec0" fontSize={13} />
              <YAxis stroke="#a0aec0" fontSize={13} />
              <Tooltip />
              <Bar dataKey="avg" fill="#6366f1" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardBox>
        {/* LineChart تطور عدد الطلاب شهريًا (بيانات حقيقية) */}
        <CardBox className="minh-220">
          <div className="card-title mb-12">{t('monthlyStudentGrowth')}</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={monthlyStudents} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="month" stroke="#a0aec0" fontSize={13} />
              <YAxis stroke="#a0aec0" fontSize={13} />
              <Tooltip />
              <Line type="monotone" dataKey="students" stroke="#22c55e" strokeWidth={3} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardBox>
        {/* BarChart مقارنة عدد الاختبارات المنجزة وغير المنجزة */}
        <CardBox className="minh-220">
          <div className="card-title mb-12">{t('completedVsUncompletedExams')}</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={examStatusData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="status" stroke="#a0aec0" fontSize={13} />
              <YAxis stroke="#a0aec0" fontSize={13} />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e42" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardBox>
      </div>
      {/* صف بجانب بعض: كرت الاختبارات الأخيرة + جدول تقارير الاختبارات */}
      <div className="main-grid-one-cols">
        {/* كرت الاختبارات الأخيرة */}
        <div className="flex-col gap-24">
          <CardBox>
            <div className="card-title mb-12">{t('recentExams')}</div>
            {examData.slice(0, 4).map((exam, i) => (
              <div key={i} className="flex-row align-center gap-12 mb-10">
                <div className="exam-avatar">{exam.title[0]}</div>
                <div className="flex-1">
                  <div className="exam-title">{exam.title}</div>
                  <div className="exam-date">{formatDateSimple(exam.date)}</div>
                </div>
                <div className="exam-score">{exam.average_score}%</div>
              </div>
            ))}
          </CardBox>
        </div>
        <br></br>
        {/* جدول تقارير الاختبارات */}
        <div className="card-box minh-220">
          <div className="card-title mb-12" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t('examReports')}</span>
            <button className="export-btn" onClick={() => downloadAsCSV(examData, `exam-report-${formatDateSimple(new Date())}`, getTableHeaders("exams"))}>
              {t('exportExamReport')}
            </button>
          </div>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>{t('examName')}</th>
                  <th>{t('date')}</th>
                  <th>{t('studentCount')}</th>
                  <th>{t('averageScore')}</th>
                  <th>{t('completionRate')}</th>
                  <th>{t('passingRate')}</th>
                </tr>
              </thead>
              <tbody>
                {examData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="no-data">
                      {t('noDataAvailable')}
                    </td>
                  </tr>
                ) : (
                  examData.map((exam, i) => (
                    <tr key={i}>
                      <td>{exam.title}</td>
                      <td>{formatDateSimple(exam.date)}</td>
                      <td>{exam.total_students}</td>
                      <td>{exam.average_score}</td>
                      <td>
                        <span className="progress-bar-bg">
                          <span
                            className="progress-bar-fill progress-bar-completion"
                            style={{ width: `${exam.completion_rate}%` }}
                          />
                        </span>
                        <span className="progress-label">
                          {exam.completion_rate}%
                        </span>
                      </td>
                      <td>{exam.passing_rate}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <br></br>


      {/* Center Column */}
      <div className="flex-col gap-24">
        <CardBox className="minh-320">
          <div className="card-title mb-12">{t('averageExamGrades')}</div>
          <div className="exam-chart-label">{t('averageScoreOverTime')}</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="date" stroke="#a0aec0" fontSize={13} />
              <YAxis stroke="#a0aec0" fontSize={13} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="average_score"
                stroke="#6366f1"
                strokeWidth={3}
                dot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardBox>
      </div>
      <br></br>

      {/* جدول تقارير الطلاب بعرض كامل */}
      <div className="table-responsive">
        <div className="card-title mb-12" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('studentReports')}</span>
          <button className="export-btn" onClick={() => downloadAsCSV(studentData, `student-report-${formatDateSimple(new Date())}`, getTableHeaders("students"))}>
            {t('exportStudentReport')}
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th>{t('studentName')}</th>
              <th>{t('examsTaken')}</th>
              <th>{t('averageScore')}</th>
              <th>{t('averageTime')}</th>
              <th>{t('lastExam')}</th>
              <th>{t('completionRate')}</th>
              <th>{t('improvement')}</th>
            </tr>
          </thead>
          <tbody>
            {studentData.length === 0 ? (
              <tr>
                <td colSpan={6} className="no-data">
                  {t('noDataAvailable')}
                </td>
              </tr>
            ) : (
              studentData.map((student, i) => (
                <tr key={i}>
                  <td>{student.name}</td>
                  <td>{student.exams_taken}</td>
                  <td>{student.average_score}</td>
                  <td>{student.average_time_taken || '00:00:00'}</td>
                  <td>{formatDateSimple(student.last_exam_date)}</td>
                  <td>
                    <span className="progress-bar-bg">
                      <span
                        className="progress-bar-fill progress-bar-completion"
                        style={{ width: `${student.completion_rate}%` }}
                      />
                    </span>
                    <span className="progress-label">
                      {student.completion_rate}%
                    </span>
                  </td>
                  <td>
                    <span className="progress-bar-bg">
                      <span
                        className="progress-bar-fill progress-bar-improvement"
                        style={{ width: `${student.improvement}%` }}
                      />
                    </span>
                    <span className="progress-label">
                      {student.improvement}%
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <style jsx>{`
        .dashboard-bg {
          min-height: 100vh;
          background: transparent;
          padding: 0;
          margin: 0;
          border-radius: 18px;
        }
        .stat-cards {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 24px;
          margin-bottom: 32px;
          padding: 32px 32px 0 32px;
        }
        .state_t-card {
          background: #eaf0fa;
          border-radius: 18px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          box-shadow: 0 2px 8px #e0e7ef;
          align-items: flex-start;
        }
        .state_t-card .icon {
          color: #6366f1;
          font-size: 22px;
          margin-bottom: 8px;
        }
        .state_t-card .value {
          font-weight: 700;
          font-size: 22px;
          color: #22223b;
        }
        .state_t-card .label {
          color: #64748b;
          font-size: 15px;
        }
        .main-grid-two-cols {
          display: grid;
          grid-template-columns: 1fr 2.5fr;
          gap: 24px;
          padding: 0 32px 32px 32px;
        }
        .main-grid > .flex-col:nth-child(2) {
          width: 100%;
          min-width: 0;
        }
        .main-grid > .flex-col:nth-child(2) .card-box,
        .main-grid > .flex-col:nth-child(2) .minh-320 {
          width: 100%;
          min-width: 0;
        }
        .flex-col {
          display: flex;
          flex-direction: column;
        }
        .flex-row {
          display: flex;
          flex-direction: row;
        }
        .gap-24 {
          gap: 24px;
        }
        .gap-12 {
          gap: 12px;
        }
        .align-center {
          align-items: center;
        }
        .mt-8 {
          margin-top: 8px;
        }
        .mb-12 {
          margin-bottom: 12px;
        }
        .mb-10 {
          margin-bottom: 10px;
        }
        .card-title {
          font-weight: 600;
          color: #64748b;
          font-size: 16px;
        }
        .big-value {
          font-size: 32px;
          font-weight: 700;
          color: #22223b;
        }
        .exam-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #6366f1;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 18px;
        }
        .exam-title {
          font-weight: 600;
          color: #22223b;
          font-size: 15px;
        }
        .exam-date {
          color: #64748b;
          font-size: 14px;
        }
        .exam-score {
          color: #a0aec0;
          font-size: 13px;
        }
        .flex-1 {
          flex: 1;
        }
        .card-box {
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 2px 8px #e0e7ef;
          padding: 24px;
          margin-bottom: 0;
        }
        .minh-220 {
          min-height: 220px;
        }
        .minh-320 {
          min-height: 320px;
        }
        .table-responsive {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 15px;
          background: white;
        }
        th,
        td {
          padding: 8px 12px;
          text-align: right;
        }
        thead tr {
          background: #f3f4f6;
        }
        tbody tr {
          border-bottom: 1px solid #e5e7eb;
          transition: background 0.2s;
        }
        tbody tr:hover {
          background: #f6f8fb;
        }
        .progress-bar-bg {
          width: 70px;
          background: #e5e7eb;
          border-radius: 6px;
          height: 10px;
          position: relative;
          display: inline-block;
          margin-left: 6px;
          vertical-align: middle;
        }
        .progress-bar-fill {
          height: 100%;
          border-radius: 6px;
          position: absolute;
          left: 0;
          top: 0;
        }
        .progress-bar-completion {
          background: #6366f1;
        }
        .progress-bar-improvement {
          background: #22c55e;
        }
        .progress-label {
          font-size: 13px;
          color: #64748b;
          margin-left: 6px;
          vertical-align: middle;
        }
        .no-data {
          text-align: center;
          padding: 16px;
          color: #a0aec0;
        }
        .exam-chart-label {
          color: #a0aec0;
          font-size: 13px;
          margin-bottom: 8px;
        }
        .export-btn {
          background: #6366f1;
          color: #fff;
          border: none;
          border-radius: 6px;
          padding: 6px 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          margin-right: 8px;
            font-family: "Zain", sans-serif;
        }
        .export-btn:hover {
          background: #4f46e5;
        }

        /* Responsive styles */
        @media (max-width: 1100px) {
          .main-grid {
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 0 8px 24px 8px;
          }
          .stat-cards {
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            padding: 16px 8px 0 8px;
          }
        }
        @media (max-width: 700px) {
          .main-grid {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 0 2px 16px 2px;
          }
          .stat-cards {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 8px 2px 0 2px;
          }
          .card-box,
          .minh-220,
          .minh-320 {
            padding: 12px !important;
            border-radius: 10px;
          }
          .card-title,
          .big-value,
          .exam-title {
            font-size: 14px;
          }
          th,
          td {
            padding: 6px 4px;
            font-size: 13px;
          }
          .progress-label {
            font-size: 11px;
          }
          .exam-avatar {
            width: 28px;
            height: 28px;
            font-size: 14px;
          }
        }
        .main-grid-two-cols {
          display: grid;
          grid-template-columns: 1fr 2.5fr;
          gap: 24px;
          padding: 0 32px 32px 32px;
        }
        @media (max-width: 900px) {
          .main-grid-two-cols {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 0 2px 16px 2px;
          }
        }
        .students-full-width {
          width: 100%;
          margin: 32px 0 0 0;
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 2px 8px #e0e7ef;
          padding: 24px;
        }
        @media (max-width: 700px) {
          .students-full-width {
            padding: 12px !important;
            border-radius: 10px;
          }
        }
        .charts-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin: 32px 0;
        }
        @media (max-width: 900px) {
          .charts-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>
    </div>
  );
};


function CardBox({ children, style }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 18,
        boxShadow: "0 2px 8px #e0e7ef",
        padding: 24,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export default Reports; 